// ProtectedRoute — gates the entire app behind authentication state.
//
// Behavior:
//   loading         — render a quiet full-page loading state
//   authenticated   — render children
//   unauthorized    — render <Unauthorized /> (signed in but no group match)
//   unauthenticated — redirect the browser to /.auth/login/aad
//   error           — render a retry banner

import { useEffect } from 'react';

import { useAuth } from './AuthProvider.jsx';
import { Unauthorized } from './Unauthorized.jsx';

function _redirectToLogin() {
  if (typeof window === 'undefined') return;
  const target = window.location.pathname + (window.location.search || '');
  const url = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(target || '/')}`;
  window.location.replace(url);
}

function FullPageLoading({ message = 'Signing you in…' }) {
  return (
    <div className="app">
      <header className="header">
        <img
          src="/PSC Group Logo Horizontal Color.png"
          alt="PSC Group"
          className="header-logo"
        />
        <div style={{ flex: 1 }} />
        <div className="header-text">
          <h1>Billing Integration</h1>
          <span className="subtitle">DAYFORCE &rarr; NETSUITE</span>
        </div>
      </header>
      <main className="main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div className="alert alert-info">{message}</div>
      </main>
    </div>
  );
}

function ErrorScreen({ error }) {
  return (
    <div className="app">
      <header className="header">
        <img
          src="/PSC Group Logo Horizontal Color.png"
          alt="PSC Group"
          className="header-logo"
        />
      </header>
      <main className="main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div className="alert alert-error" style={{ maxWidth: 560, padding: '20px 24px' }}>
          <h2 style={{ marginTop: 0 }}>Couldn't reach sign-in service</h2>
          <p>The app couldn't load your identity. {error?.message ? <code>{error.message}</code> : null}</p>
          <p>Try reloading the page, or sign out and sign in again:</p>
          <p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>Reload</button>
            {' '}
            <a href="/.auth/logout" className="btn btn-secondary">Sign out</a>
          </p>
        </div>
      </main>
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { status, principal, error } = useAuth();

  useEffect(() => {
    if (status === 'unauthenticated') _redirectToLogin();
  }, [status]);

  if (status === 'loading')        return <FullPageLoading />;
  if (status === 'unauthenticated') return <FullPageLoading message="Redirecting to sign-in…" />;
  if (status === 'unauthorized')   return <Unauthorized userName={principal?.userName} />;
  if (status === 'error')          return <ErrorScreen error={error} />;

  return children;
}
